/**
 * Types for the admin-only web scraping tool.
 *
 * Mirrors the API's ScrapeProfileDtos. A profile has two halves — how to fetch a page and how to
 * interpret it — and a run records what came back from each half so a developer can tell which one
 * broke.
 */

/** Navigation completion condition. NetworkIdle is the default: the target sites are JS-rendered. */
/**
 * What a profile is for, and therefore which code picks it up. A ScheduleExtraction prompt must
 * produce the pipe-delimited game rows the itinerary parser expects; a Spike prompt emits whatever
 * JSON its operator wanted, so the two are not interchangeable.
 */
export type ScrapeProfilePurpose = 'Spike' | 'ScheduleExtraction';

/**
 * Which fetcher reads a site. Our renderer is free and keeps the page in our network but is refused by
 * bot protection; Firecrawl clears that protection, costs a credit per call, and sends the URL to a
 * third party.
 */
export type ScrapeFetchStrategy = 'RenderThenFirecrawl' | 'RenderOnly' | 'FirecrawlOnly';

export type ScrapeWaitUntil = 'Load' | 'DomContentLoaded' | 'NetworkIdle';

export type ScrapeRunStatus =
  | 'Pending'
  | 'Rendering'
  | 'Extracting'
  | 'Completed'
  | 'Failed';

/**
 * Which half of the profile failed — the field that answers "was it the page or the prompt?".
 *
 * `Fetch` means headless Chromium never produced usable content, so the prompt was never exercised.
 * `Extraction` means the page rendered (and its content is on the run) but the model's reply was
 * unusable. `None` accompanies any non-failed run.
 */
export type ScrapeFailureStage = 'None' | 'Fetch' | 'Extraction';

/** robots.txt verdict. Recorded for visibility, never enforced — see the API's RobotsTxtService. */
export type RobotsCheckStatus =
  | 'NotChecked'
  | 'Allowed'
  | 'Disallowed'
  | 'Unavailable';

/** The editable shape of a profile. Used for both create and update. */
export interface ScrapeProfileRequest {
  name: string;
  description?: string | null;
  url: string;
  waitUntil: ScrapeWaitUntil;
  waitForSelector?: string | null;
  waitMilliseconds: number;
  timeoutSeconds: number;
  viewportWidth: number;
  viewportHeight: number;
  userAgent?: string | null;
  nextPageSelector?: string | null;
  maxPages: number;
  checkRobots: boolean;
  extractionPrompt: string;
  /** Omitted means "Spike", matching the server's default. */
  purpose?: ScrapeProfilePurpose;
  /**
   * Host this profile applies to, matched as a suffix on whole labels — "tourneymachine.com"
   * covers "www.tourneymachine.com" but never "nottourneymachine.com". Blank means the profile is
   * only reachable as the default.
   */
  hostPattern?: string | null;
  /** The fallback for this purpose when no host matches. At most one per purpose. */
  isDefault?: boolean;
  /** Omitted means "RenderThenFirecrawl", matching the server's default. */
  fetchStrategy?: ScrapeFetchStrategy;
  /** Optional regex applied to a pasted URL before it is read, e.g. to reach a site's games view. */
  urlRewritePattern?: string | null;
  /** Replacement for the pattern; supports $1-style groups. */
  urlRewriteReplacement?: string | null;
}

export interface ScrapeProfile {
  id: string;
  name: string;
  description: string | null;
  url: string;
  waitUntil: ScrapeWaitUntil;
  waitForSelector: string | null;
  waitMilliseconds: number;
  timeoutSeconds: number;
  viewportWidth: number;
  viewportHeight: number;
  userAgent: string | null;
  nextPageSelector: string | null;
  maxPages: number;
  checkRobots: boolean;
  extractionPrompt: string;
  purpose: ScrapeProfilePurpose;
  hostPattern: string | null;
  isDefault: boolean;
  fetchStrategy: ScrapeFetchStrategy;
  urlRewritePattern: string | null;
  urlRewriteReplacement: string | null;
  isArchived: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Null when the profile has never been run. */
  latestRun: ScrapeRunSummary | null;
}

/** A run without its payloads — enough to render a history row. */
export interface ScrapeRunSummary {
  id: string;
  scrapeProfileId: string;
  url: string;
  status: ScrapeRunStatus;
  failureStage: ScrapeFailureStage;
  robotsStatus: RobotsCheckStatus;
  robotsDetail: string | null;
  pagesRendered: number;
  pageTitle: string | null;
  httpStatus: number | null;
  waitForSelectorTimedOut: boolean;
  extractionJsonValid: boolean;
  errorMessage: string | null;
  renderMs: number | null;
  extractionMs: number | null;
  llmProvider: string | null;
  llmModel: string | null;
  tokensUsed: number | null;
  /**
   * True once retention pruning dropped this run's page bodies. The UI uses it to say "pruned"
   * rather than showing an empty panel that looks like a render failure.
   */
  rawContentPruned: boolean;
  createdAt: string;
  completedAt: string | null;
}

/**
 * A run with everything, for the side-by-side view. Returning raw content and extracted JSON
 * together is what makes a failure diagnosable rather than merely reported.
 */
export interface ScrapeRunDetail extends ScrapeRunSummary {
  rawHtml: string | null;
  renderedText: string | null;
  rawLlmResponse: string | null;
  extractedJson: string | null;
  fetchConfigSnapshot: string | null;
  extractionPromptSnapshot: string | null;
}

/** Bounds mirroring the API's ScrapeProfileLimits, so the form rejects what the server would. */
export const SCRAPE_LIMITS = {
  minTimeoutSeconds: 1,
  maxTimeoutSeconds: 120,
  maxWaitMilliseconds: 30_000,
  minViewport: 320,
  maxViewport: 4096,
  minPages: 1,
  maxPages: 20,
  maxNameLength: 200,
  maxSelectorLength: 500,
  maxUserAgentLength: 500,
} as const;

export const WAIT_UNTIL_OPTIONS: ReadonlyArray<{
  value: ScrapeWaitUntil;
  label: string;
  hint: string;
}> = [
  {
    value: 'NetworkIdle',
    label: 'Network idle',
    hint: 'Waits for XHRs to settle. Best default for JS-rendered schedule sites.',
  },
  {
    value: 'Load',
    label: 'Load',
    hint: 'Waits for the load event. Cheaper, but often captures an app shell.',
  },
  {
    value: 'DomContentLoaded',
    label: 'DOM content loaded',
    hint: 'Fastest. Only useful for server-rendered pages.',
  },
];

/** The starting point for a new profile — the defaults that work for the first target class. */
export const NEW_PROFILE_DEFAULTS: ScrapeProfileRequest = {
  name: '',
  description: '',
  url: '',
  waitUntil: 'NetworkIdle',
  waitForSelector: '',
  waitMilliseconds: 0,
  timeoutSeconds: 30,
  viewportWidth: 1280,
  viewportHeight: 1024,
  userAgent: '',
  nextPageSelector: '',
  maxPages: 1,
  checkRobots: true,
  purpose: 'Spike',
  hostPattern: '',
  isDefault: false,
  fetchStrategy: 'RenderThenFirecrawl',
  urlRewritePattern: '',
  urlRewriteReplacement: '',
  extractionPrompt:
    'Extract every scheduled event on this page. For each one return the date, ' +
    'start time, timezone if stated, field or venue name, and the two team names. ' +
    'Return an object with an "events" array.',
};
